import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
  createTheme,
} from "@mui/material";

export default function ThemeProvider({ children }) {
  const mode = "light";

  /**
   * @type {import("@mui/material").Theme & import("@mui/x-charts/themeAugmentation") & import("@mui/x-data-grid/themeAugmentation") & import("@mui/x-tree-view/themeAugmentation")}
   */
  const theme = createTheme({
    typography: {
      fontFamily: "Inter,sans-serif",
      h1: {
        fontSize: "5.25rem",
        fontWeight: "600",
      },
      h2: {
        fontSize: "4.25rem",
        fontWeight: "600",
      },
      h3: {
        fontSize: "3.5rem",
        fontWeight: "600",
      },
      h4: {
        fontSize: "3rem",
        fontWeight: "600",
      },
      h5: {
        fontSize: "2.5rem",
        fontWeight: "600",
      },
      h6: {
        fontSize: "2rem",
        fontWeight: "600",
      },
      body1: {
        fontSize: "0.875rem",
        fontWeight: "500",
      },
      body2: {
        fontSize: "0.875rem",
        fontWeight: "400",
      },
    },
    // Can't use CSS Variables here :(
    palette: {
      mode,

      primary: {
        main: mode === "dark" ? "#4281f8" : "#597cec",
        secondary: "#314482",
      },
      secondary: {
        main: mode === "dark" ? "#f2f3f4" : "#0b1845",
        secondary: "#060D26",
      },
      success: {
        main: "#40c067",
      },
      info: {
        main: "#597cec",
      },
      warning: {
        main: "#ff9800",
      },
      error: {
        main: "#fb6746",
      },
      "background-light": {
        main: mode === "dark" ? "#262628" : "#f6f8fc",
        contrastText: "#f6f8fc",
      },
      "primary-light": {
        main: mode === "dark" ? "#353538" : "#eef2fd",
        contrastText: "#f6f8fc",
      },
      "background-soft": {
        main: mode === "dark" ? "#2e2e31" : "#dde4fb",
        contrastText: "#f6f8fc",
      },
      "background-dark": {
        main: "#212123",
        contrastText: "#f6f8fc",
      },
    },
    components: {
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            borderRadius: "calc(infinity * 1px)",
            backgroundColor: "var(--color-blue-50)",
            border: "1px solid var(--color-dark-blue-50)",
            width: "fit-content",
            gap: "calc(var(--spacing) * 1)",
            padding: "calc(var(--spacing) * 0.5)",
          },
          grouped: {
            borderRadius: "calc(infinity * 1px)",
          },
        },
      },

      MuiTypography: {
        styleOverrides: {
          root: {
            fontWeight: "var(--font-weight-medium)",
            fontSize: "var(--text-sm)",
            ...(mode === "dark" && {
              color: "var(--color-secondary)",
            }),
          },
        },
      },
      MuiSvgIcon: {
        defaultProps: {
          fontSize: "medium",
        },
        styleOverrides: {
          fontSizeSmall: {
            height: "calc(var(--spacing) * 3)",
            width: "calc(var(--spacing) * 3)",
          },
          fontSizeMedium: {
            height: "calc(var(--spacing) * 3.5)",
            width: "calc(var(--spacing) * 3.5)",
          },
          fontSizeLarge: {
            height: "calc(var(--spacing) * 4)",
            width: "calc(var(--spacing) * 4)",
          },
        },
        variants: [
          {
            props: { fontSize: "extra-large" },
            style: {
              height: "calc(var(--spacing) * 5)",
              width: "calc(var(--spacing) * 5)",
            },
          },
        ],
      },
      MuiStepIcon: {
        defaultProps: {
          fontSize: "medium",
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderRadius: "var(--radius-2xl)",
            paddingLeft: "calc(var(--spacing) * 2)",
            paddingRight: "calc(var(--spacing) * 2)",
            border: "1px solid var(--color-dark-blue-50)",
            backgroundColor:
              mode === "dark" ? "var(--color-background-light)" : "white",
            backgroundImage: "none",
            ":before": {
              backgroundColor: "transparent",
            },
            ":first-of-type": {
              borderTopLeftRadius: "var(--radius-2xl)",
              borderTopRightRadius: "var(--radius-2xl)",
            },
            ":last-of-type": {
              borderBottomLeftRadius: "var(--radius-2xl)",
              borderBottomRightRadius: "var(--radius-2xl)",
            },
            "&.Mui-expanded": {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          content: {
            fontSize: "var(--text-base)",
            fontWeight: "var(--font-weight-medium)",
            color: "var(--color-secondary)",
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            fontSize: "var(--text-sm)",
            color: "var(--color-dark-blue-400)",
            backgroundColor: "var(--color-background-light)",
            fontWeight: "var(--font-weight-medium)",
            borderRadius: "var(--radius-2xl)",
            marginBottom: "calc(var(--spacing) * 4)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "capitalize",
            fontSize: "var(--text-xs)",
            "& .MuiButton-startIcon > .MuiSvgIcon-root": {
              height: "calc(var(--spacing) * 3.5)",
              width: "calc(var(--spacing) * 3.5)",
            },
          },
          sizeSmall: {
            minHeight: "calc(var(--spacing) * 6)",
            padding: "0px calc(var(--spacing) * 2)",
          },
          sizeMedium: {
            minHeight: "calc(var(--spacing) * 7)",
            padding: "0px calc(var(--spacing) * 3)",
          },
          sizeLarge: {
            minHeight: "calc(var(--spacing) * 8)",
            padding: "0px calc(var(--spacing) * 3)",
            fontSize: "var(--text-sm)",
            "& .MuiButton-startIcon > .MuiSvgIcon-root": {
              height: "calc(var(--spacing) * 4)",
              width: "calc(var(--spacing) * 4)",
            },
          },
        },
        variants: [
          {
            props: { size: "extra-large" },
            style: {
              minHeight: "calc(var(--spacing) * 9)",
              fontSize: "var(--text-sm)",
              padding: "0px calc(var(--spacing) * 4)",
            },
          },
          {
            props: { variant: "contained" },
            style: {
              borderRadius: "calc(infinity * 1px)",
              fontWeight: "var(--font-weight-medium)",
              boxShadow: "none",
              ":hover": {
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "outlined" },
            style: {
              borderRadius: "calc(infinity * 1px)",
              fontWeight: "var(--font-weight-medium)",
              boxShadow: "none",
              ":hover": {
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "contained", color: "primary" },
            style: {
              backgroundColor: "var(--color-primary)",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-900)"
                    : "var(--color-blue-200)",
                color: "white",
              },
            },
          },
          {
            props: { variant: "contained", color: "secondary" },
            style: {
              backgroundColor: "var(--color-secondary)",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-dark-blue-900)"
                    : "var(--color-dark-blue-200)",
                color: "white",
              },
            },
          },
          {
            props: { variant: "contained", color: "primary-light" },
            style: {
              backgroundColor: "var(--color-blue-50)",
              color: "var(--color-secondary)",
              border: "1px solid var(--color-dark-blue-50)",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-dark-blue-300)"
                    : "var(--color-slate-100)",
                color: mode === "dark" ? "white" : "var(--color-gray-400)",
              },
            },
          },
          {
            props: {
              variant: "contained",
              color: "background-soft",
            },
            style: {
              backgroundColor: "var(--color-blue-75)",
              color: "var(--color-secondary)",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-slate-700)"
                    : "var(--color-slate-100)",
                color: "var(--color-gray-400)",
              },
            },
          },
          {
            props: { variant: "outlined", color: "primary-light" },
            style: {
              backgroundColor:
                mode === "dark" ? "var(--color-blue-50)" : "white",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor:
                mode === "dark"
                  ? "var(--color-dark-blue-100)"
                  : "var(--color-dark-blue-50)",
              color: "var(--color-secondary)",
              boxShadow: "none",
              ":disabled": {
                backgroundColor:
                  mode === "dark" ? "var(--color-blue-50)" : "white",
                color:
                  mode === "dark"
                    ? "var(--color-gray-900)"
                    : "var(--color-gray-400)",
              },
            },
          },
          {
            props: { variant: "outlined", color: "primary" },
            style: {
              borderColor: "var(--color-secondary)",
              color: "var(--color-secondary)",
              ":disabled": {
                backgroundColor: "transparent",
                borderColor: "var(--color-dark-blue-200)",
                color: "var(--color-dark-blue-200)",
              },
            },
          },
          {
            props: { variant: "outlined", color: "secondary" },
            style: {
              borderColor:
                mode === "dark"
                  ? "var(--color-dark-blue-300)"
                  : "var(--color-primary)",
              color:
                mode === "dark"
                  ? "var(--color-dark-blue-300)"
                  : "var(--color-primary)",
              ":disabled": {
                backgroundColor: "transparent",
                borderColor:
                  mode === "dark"
                    ? "var(--color-dark-blue-50)"
                    : "var(--color-blue-200)",
                color:
                  mode === "dark"
                    ? "var(--color-dark-blue-50)"
                    : "var(--color-blue-200)",
              },
            },
          },
          {
            props: { variant: "square" },
            style: {
              borderRadius: "var(--radius-md)",
              fontWeight: "var(--font-weight-medium)",
              boxShadow: "none",
              backgroundColor: "var(--color-primary)",
              color: "white",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-900)"
                    : "var(--color-blue-200)",
                color: "white",
              },
              ":hover": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-300)"
                    : "var(--color-blue-600)",
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "square", color: "primary" },
            style: {
              backgroundColor: "var(--color-primary)",
              color: "white",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-900)"
                    : "var(--color-blue-200)",
                color: "white",
              },
              ":hover": {
                backgroundColor: "var(--color-blue-600)",
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "square", color: "secondary" },
            style: {
              backgroundColor: "var(--color-secondary)",
              color: "white",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-dark-blue-900)"
                    : "var(--color-dark-blue-200)",
                color: "white",
              },
              ":hover": {
                backgroundColor: "var(--color-dark-blue-600)",
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "square", color: "primary-light" },
            style: {
              backgroundColor: "var(--color-blue-50)",
              color: "var(--color-secondary)",
              border: "1px solid var(--color-dark-blue-50)",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-50)"
                    : "var(--color-slate-100)",
                color:
                  mode === "dark"
                    ? "var(--color-dark-blue-300)"
                    : "var(--color-gray-400)",
              },
              ":hover": {
                backgroundColor: "var(--color-blue-75)",
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "square-outlined" },
            style: {
              borderRadius: "var(--radius-md)",
              fontWeight: "var(--font-weight-medium)",
              boxShadow: "none",
              backgroundColor: "white",
              border: "1px solid var(--color-primary)",
              color: "var(--color-primary)",
              ":disabled": {
                borderColor: "var(--color-blue-200)",
                color: "var(--color-blue-200)",
              },
              ":hover": {
                borderColor: "var(--color-blue-600)",
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "square-outlined", color: "primary" },
            style: {
              backgroundColor:
                mode === "dark" ? "var(--color-background-dark)" : "white",
              border: "1px solid var(--color-primary)",
              color: "var(--color-primary)",
              ":disabled": {
                borderColor: "var(--color-blue-200)",
                color: "var(--color-blue-200)",
              },
              ":hover": {
                borderColor: "var(--color-blue-600)",
                boxShadow: "none",
              },
            },
          },
          {
            props: {
              variant: "square-outlined",
              color: "secondary",
            },
            style: {
              backgroundColor:
                mode === "dark" ? "var(--color-background-light)" : "white",
              border: "1px solid var(--color-secondary)",
              color: "var(--color-secondary)",
              ":disabled": {
                borderColor: "var(--color-dark-blue-200)",
                color: "var(--color-dark-blue-200)",
              },
              ":hover": {
                borderColor: "var(--color-dark-blue-600)",
                boxShadow: "none",
              },
            },
          },
          {
            props: {
              variant: "square-outlined",
              color: "primary-light",
            },
            style: {
              backgroundColor: "var(--color-blue-50)",
              color: "var(--color-secondary)",
              border: "1px solid var(--color-dark-blue-50)",
              ":disabled": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-50)"
                    : "var(--color-slate-100)",
                color:
                  mode === "dark"
                    ? "var(--color-dark-blue-300)"
                    : "var(--color-gray-400)",
              },
              ":hover": {
                backgroundColor:
                  mode === "dark"
                    ? "var(--color-blue-50)"
                    : "var(--color-blue-75)",
                boxShadow: "none",
                borderColor: "var(--color-blue-100)",
              },
            },
          },
          {
            props: { variant: "citation" },
            style: {
              borderRadius: "var(--radius-sm)",
              color: "var(--color-dark-blue-400)",
              backgroundColor: "var(--color-blue-50)",
              padding:
                "calc(var(--spacing) * 0.25) calc(var(--spacing) * 0.625)",
              fontSize: "var(--text-2xs)",
              height: "calc(var(--spacing) * 4.25)",
              ":hover": {
                borderColor: "var(--color-dark-blue-100)",
                color: "var(--color-secondary)",
              },
            },
          },
        ],
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            padding: "0",
            "& .MuiSvgIcon-root": {
              height: "calc(var(--spacing) *  3.5)",
              width: "calc(var(--spacing) *  3.5)",
            },
          },
          sizeSmall: {
            fontSize: "var(--text-xs)",
            height: "calc(var(--spacing) * 6)",
            width: "calc(var(--spacing) * 6)",
          },
          sizeMedium: {
            fontSize: "var(--text-sm)",
            height: "calc(var(--spacing) * 7)",
            width: "calc(var(--spacing) * 7)",
          },
          sizeLarge: {
            fontSize: "var(--text-base)",
            height: "calc(var(--spacing) * 8)",
            width: "calc(var(--spacing) * 8)",
          },
        },
        variants: [
          {
            props: { size: "extra-large" },
            style: {
              fontSize: "1.125rem",
              height: "calc(var(--spacing) * 9)",
              width: "calc(var(--spacing) * 9)",
            },
          },
        ],
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            padding: "calc(var(--spacing) * 0.5)",
            fontSize: "var(--text-2xs)",
            borderRadius: "calc(infinity * 1px)",
            color: "white",
            backgroundColor: "var(--color-primary)",
          },
          colorSecondary: {
            backgroundColor: "var(--color-secondary)",
          },
          colorSuccess: {
            backgroundColor: "var(--color-success)",
          },
          colorInfo: {
            backgroundColor: "var(--color-info)",
          },
          colorError: {
            backgroundColor: "var(--color-error)",
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            minWidth: "calc(var(--spacing) * 5)",
            height: "calc(var(--spacing) * 5)",
            fontSize: "var(--text-sm)",
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            "&.MuiTablePagination-toolbar": {
              paddingLeft: "calc(var(--spacing) * 2.5)",
            },
          },
        },
      },

      MuiFormControl: {
        styleOverrides: {
          root: {
            display: "relative",
            border: 0,
            paddingBottom: "0 !important",
            backgroundColor:
              mode === "dark" ? "var(--color-background-light)" : "white",
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            paddingBottom: 0,
            marginBottom: 0,
            height: "calc(var(--spacing) * 8)",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor:
              mode === "dark" ? "var(--color-background-light)" : "white",
            "& input": {
              color: "var(--color-secondary)",
              height: "calc(var(--spacing) * 6)",
            },
            "& label": {
              color: "var(--color-dark-blue-300)",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor:
                  mode === "dark"
                    ? "var(--color-dark-blue-50)"
                    : "var(--color-dark-blue-100)",
              },
              "&:hover fieldset": {
                borderColor: "var(--color-primary)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "var(--color-primary)",
              },
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          // Removing placeholder to floating label style
          root: {
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-medium)",
            transform: "none",
            position: "static",
            marginTop: "calc(var(--spacing) * 2)",
            marginBottom: "calc(var(--spacing) * 3)",
            marginLeft: "calc(var(--spacing) * 3)",
          },
          standard: {
            "+ .MuiInputBase-root": {
              marginTop: "calc(var(--spacing) * 2)",
            },
            margin: "0 !important", // Standard Input Don't Have Top Borders
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            "& .MuiSvgIcon-root": {
              height: "calc(var(--spacing) * 6)",
              width: "calc(var(--spacing) * 6)",
            },
          },
          outlined: {
            "~ .MuiOutlinedInput-notchedOutline": {
              borderRadius: "var(--radius-lg)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-medium)",
            borderRadius: "var(--radius-lg)",
            ...(mode === "dark" && {
              "::placeholder": {
                color: "var(--color-gray-400)",
              },
            }),
          },
          notchedOutline: {
            top: "0px",
            borderRadius: "var(--radius-lg)",
            "& legend": {
              display: "none",
            },
          },
        },
        variants: [
          {
            props: { size: "small" },
            style: {
              margin: 0,
            },
          },
        ],
        defaultProps: {
          size: "small",
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          inputRoot: {
            "> .MuiOutlinedInput-notchedOutline": {
              borderRadius: "var(--radius-lg)",
            },
          },
          paper: {
            boxShadow: "var(--shadow-lg)",
            border:
              mode === "dark"
                ? "1px solid var(--color-dark-blue-50)"
                : "1px solid var(--color-gray-200)",
            borderRadius: "var(--radius-2xl)",
            // When option is focused using keyboard
            "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
              bgcolor: "var(--color-blue-50)",
              borderLeft: "2px solid",
              borderLeftColor: "var(--color-primary)",
            },
          },
          option: {
            padding: "calc(var(--spacing) * 3)",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-medium)",
            backgroundColor:
              mode === "dark" ? "var(--color-background-light)" : "white",
            ":hover": {
              borderLeft: "2px solid var(--color-primary)",
              backgroundColor: "var(--color-blue-50)",
            },
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          slotProps: {
            paper: {
              elevation: 0,
            },
          },
        },
        styleOverrides: {
          paper: {
            padding: "calc(var(--spacing) * 2)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--color-dark-blue-50)",
            backgroundColor:
              mode === "dark" ? "var(--color-background-light)" : "white",
            boxShadow: "var(--shadow-sm)",
          },
          list: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        },
      },
      MuiMenuList: {
        styleOverrides: {
          root: {
            paddingTop: 0,
            paddingBottom: 0,
            borderRadius: "var(--radius-2xl)",
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: "calc(var(--spacing) * 1) calc(var(--spacing) * 2)",
            marginTop: "calc(var(--spacing) * 0.5)",
            marginBottom: "calc(var(--spacing) * 0.5)",
            fontWeight: "var(--font-weight-medium)",
            borderRadius: "var(--radius-lg)",
            fontSize: "var(--text-sm)",
            color:
              mode === "dark"
                ? "var(--color-dark-blue-400)"
                : "var(--color-secondary)",
            "&.Mui-selected": {
              backgroundColor: "var(--color-blue-50)",
            },
            ":hover": {
              backgroundColor: "var(--color-blue-50) !important",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: "unset",
            backgroundColor: "var(--color-blue-50)",
            borderRadius: "calc(infinity * 1px)",
            "& .Mui-selected": {
              color: "white !important",
              zIndex: 1,
            },
            "& .MuiTouchRipple-root": {
              borderRadius: "calc(infinity * 1px)",
            },
            ":hover": {
              backgroundColor:
                mode === "dark"
                  ? "var(--color-dark-blue-100)"
                  : "var(--color-blue-75)",
            },
          },
          flexContainer: {
            height: "100%",
          },
          indicator: {
            borderRadius: "calc(infinity * 1px)",
            backgroundColor: "var(--color-primary)",
            height: "100%",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            maxWidth: "none",
            minHeight: "auto",
            height: "100%",
            color: "var(--color-dark-blue-300)", // default tab color
            textTransform: "none",
            fontWeight: "var(--font-weight-medium)",
            fontSize: "var(--text-sm)",
            padding: "calc(var(--spacing) * 2.5) calc(var(--spacing) * 5)",
            transition: "color 0.2s ease-in-out",
            ":hover": {
              color: "var(--color-secondary)",
            },
          },
        },
      },
    },
  });

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
      <CssBaseline />
    </StyledEngineProvider>
  );
}
